import { useCallback } from "react";
import useSWR from "swr";
import { FileInfo, Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { LeasingCompanyDocument, RenameLeasingCompanyDocument } from "./types";

export const useLeasingCompanyDocumentsApi = () => {
  const api = useApi();

  const useLeasingCompanyDocuments = useCallback(
    (query: PaginationQuery, signal: AbortSignal, leasingCompanyId?: string) => {
      const url = buildUrl(
        leasingCompanyId
          ? `/admin/leasing_companies/${leasingCompanyId}/documents`
          : "/admin/leasing_company_documents",
        query,
      );
      return useSWR<Paginated<LeasingCompanyDocument>, CustomError, string>(url, (url) =>
        api.get<Paginated<LeasingCompanyDocument>>({ url, signal }),
      );
    },
    [api],
  );

  const deleteLeasingCompanyDocument = useCallback(
    async (documentId: string, leasingCompanyId?: string): Promise<void> => {
      const url = `/admin/leasing_company_documents/${documentId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/leasing_company_documents`, `/admin/leasing_companies/${leasingCompanyId}/documents`]);
      });
    },
    [api],
  );

  const insertLeasingCompanyDocument = useCallback(
    async (file: File, overWrite?: boolean, leasingCompanyId?: string): Promise<void> => {
      const formData = new FormData();
      formData.append("file", file, file.name);

      await api.postNoResult<FormData>(
        `/admin/leasing_companies/${leasingCompanyId}/documents${overWrite ? "?overwrite=1" : ""}`,
        formData,
      );
      api.mutateCache([`/admin/leasing_companies/${leasingCompanyId}/documents`, "/admin/leasing_company_documents"]);
    },
    [api],
  );

  const getLeasingCompanyDocument = useCallback(
    (docID: string, signal: AbortSignal) => {
      const url = `/admin/leasing_company_documents/${docID}`;
      const r = api.get<FileInfo>({ url, signal });
      return r;
    },
    [api],
  );

  const renameDocument = useCallback(
    async (docId: string, body: RenameLeasingCompanyDocument, companyId?: string): Promise<void> => {
      await api.put<RenameLeasingCompanyDocument>(`/admin/leasing_company_documents/${docId}`, body);
      api.mutateCache([`/admin/leasing_companies/${companyId}/documents`, "/admin/leasing_company_documents"]);
    },
    [api],
  );

  return {
    useLeasingCompanyDocuments,
    deleteLeasingCompanyDocument,
    insertLeasingCompanyDocument,
    getLeasingCompanyDocument,
    renameDocument,
  };
};
